import { MenuButton, MenuDropdown } from '#components'
import { useI18n, useLocalePath } from '#i18n'
import { computed } from 'vue'

export const useMainMenu = () => {
  const { t } = useI18n()
  const { userData } = useAuthData()

  const menuConfig = computed(() =>
    ['about', 'events', 'members', 'uiaIrol', 'network', 'resources'].map((key) => ({
      dropdown: key,
      element: MenuButton,
      component: MenuDropdown,
      path: menuMain.value[key]?.path ?? null,
      layout: menuMain.value[key]?.layout ?? null
    }))
  )

  const menuTop = computed(() => {
    const baseMenu = [
      {
        title: t('menu.MenuTop.home'),
        items: [{ title: t('menu.MenuTop.home'), path: 'index' }]
      },
      {
        title: t('menu.MenuTop.articles'),
        items: [{ title: t('menu.MenuTop.articles'), path: 'articles' }]
      },
      {
        title: t('menu.MenuTop.sponsorship'),
        items: [
          { title: t('menu.MenuTop.partnerBenefits'), path: 'pages-sponsorship', hash: '#tab1' },
          { title: t('menu.MenuTop.eventPartnerships'), path: 'pages-sponsorship', hash: '#tab2' }
        ]
      }
    ]

    return baseMenu
  })

  const menuMain = computed(() => {
    const localePath = useLocalePath()
    return {
      about: {
        title: t('menu.mainMenu.about'),
        path: localePath({ name: 'pages-who-we-are' }),
        menuCss: 'w-120',
        gridCss: 'grid-cols-2',
        items: [
          {
            title: t('menu.sectionAbout.whoWeAre'),
            path: 'pages-who-we-are',
            icon: 'address-card',
            iconCss: 'text-teal-500 bg-teal-500/20',
            linkCss: 'group-hover:text-teal-600 [&.router-link-exact-active]:text-teal-600'
          },
          {
            title: t('menu.sectionAbout.governance'),
            path: 'pages-governance',
            icon: 'school-flag',
            iconCss: 'text-blue-600 bg-blue-600/20',
            linkCss: 'group-hover:text-blue-600 [&.router-link-exact-active]:text-blue-600'
          },
          {
            title: t('menu.sectionAbout.charters'),
            path: 'charters-and-resolutions',
            icon: 'chart-simple',
            iconCss: 'text-orange-500 bg-orange-500/20',
            linkCss: 'group-hover:text-orange-600 [&.router-link-exact-active]:text-orange-600'
          },
          {
            title: t('menu.sectionAbout.uiaFund'),
            path: 'pages-endowment-fund',
            icon: 'handshake',
            iconCss: 'text-emerald-500 bg-emerald-500/20',
            linkCss: 'group-hover:text-emerald-600 [&.router-link-exact-active]:text-emerald-600'
          },
          {
            title: t('menu.sectionAbout.press'),
            path: 'press',
            icon: 'newspaper',
            iconCss: 'text-indigo-500 bg-indigo-500/20',
            linkCss: 'group-hover:text-indigo-600 [&.router-link-exact-active]:text-indigo-600'
          }
        ]
      },
      events: {
        title: t('menu.mainMenu.events'),
        menuCss: 'w-120',
        gridCss: 'grid-cols-2',
        path: localePath({ name: 'events' }),
        items: [
          {
            title: t('menu.sectionEvents.calendar'),
            path: 'events',
            icon: 'calendar-users',
            iconCss: 'text-orange-500 bg-orange-500/20',
            linkCss: 'group-hover:text-orange-600 [&.router-link-exact-active]:text-orange-600'
          },
          {
            title: t('menu.sectionEvents.uiaAcademy'),
            path: 'pages-uiacademy',
            icon: 'user-graduate',
            iconCss: 'text-teal-500 bg-teal-500/20',
            linkCss: 'group-hover:text-teal-600 [&.router-link-exact-active]:text-teal-600'
          },
          {
            title: t('menu.sectionEvents.events'),
            path: 'pages-learn-more',
            icon: 'party-horn',
            iconCss: 'text-pink-500 bg-pink-500/20',
            linkCss: 'group-hover:text-pink-600 [&.router-link-exact-active]:text-pink-600'
          },
          {
            title: t('menu.sectionEvents.partnerEvents'),
            path: 'partner-events',
            icon: 'handshake',
            iconCss: 'text-purple-500 bg-purple-500/20',
            linkCss: 'group-hover:text-purple-600 [&.router-link-exact-active]:text-purple-600'
          }
        ]
      },
      members: {
        title: t('menu.mainMenu.members'),
        menuCss: 'w-120',
        gridCss: 'grid-cols-2',
        path: localePath({ name: 'pages-join-the-uia' }),
        items: [
          {
            title: t('menu.sectionMembers.becomeMember'),
            path: 'pages-become-uia-member',
            icon: 'id-card',
            iconCss: 'text-emerald-500 bg-emerald-500/20',
            linkCss: 'group-hover:text-emerald-600 [&.router-link-exact-active]:text-emerald-600'
          },
          {
            title: t('menu.sectionMembers.joinUia'),
            path: 'pages-join-the-uia',
            icon: 'user-plus',
            iconCss: 'text-indigo-500 bg-indigo-500/20',
            linkCss: 'group-hover:text-indigo-600 [&.router-link-exact-active]:text-indigo-600'
          },
          {
            title: t('menu.sectionMembers.directory'),
            path: 'members',
            icon: 'users-viewfinder',
            iconCss: 'text-blue-500 bg-blue-500/20',
            linkCss: 'group-hover:text-blue-600 [&.router-link-exact-active]:text-blue-600'
          },
          {
            title: t('menu.sectionMembers.sponsorColleague'),
            path: 'membership-refer-a-friend',
            icon: 'user-group',
            iconCss: 'text-sky-500 bg-sky-500/20',
            linkCss: 'group-hover:text-sky-600 [&.router-link-exact-active]:text-sky-600'
          }
        ]
      },
      uiaIrol: {
        title: t('menu.mainMenu.uiaIrol'),
        menuCss: 'w-120',
        gridCss: 'grid-cols-2',
        path: localePath({ name: 'actions' }),
        layout: 'irol',
        items: [
          {
            title: t('menu.sectionUiaIrol.actions'),
            path: 'actions',
            icon: 'circle-chevron-up',
            iconCss: 'text-teal-500 bg-teal-500/20',
            linkCss: 'group-hover:text-teal-600 [&.router-link-exact-active]:text-teal-600',
            layout: 'irol'
          },
          {
            title: t('menu.sectionUiaIrol.presentation'),
            path: 'uia-irol',
            icon: 'gavel',
            iconCss: 'text-blue-500 bg-blue-500/20',
            linkCss: 'group-hover:text-blue-600 [&.router-link-exact-active]:text-blue-600',
            layout: 'irol'
          }
        ]
      },
      network: {
        title: t('menu.mainMenu.network'),
        path: localePath({ name: 'commissions' }),
        menuCss: 'w-120',
        gridCss: 'grid-cols-2',
        items: [
          {
            title: t('menu.sectionNetwork.commissions'),
            path: 'commissions',
            icon: 'people-roof',
            iconCss: 'text-blue-500 bg-blue-500/20',
            linkCss: 'group-hover:text-blue-600 [&.router-link-exact-active]:text-blue-600'
          },
          {
            title: t('menu.sectionNetwork.comitties'),
            path: 'committees',
            icon: 'people-line',
            iconCss: 'text-teal-500 bg-teal-500/20',
            linkCss: 'group-hover:text-teal-600 [&.router-link-exact-active]:text-teal-600'
          },
          {
            title: t('menu.sectionNetwork.worldUia'),
            path: 'national-committees',
            icon: 'globe-stand',
            iconCss: 'text-orange-500 bg-orange-500/20',
            linkCss: 'group-hover:text-orange-600 [&.router-link-exact-active]:text-orange-600'
          },
          {
            title: t('menu.sectionNetwork.barsAndOrganizations'),
            path: 'pages-bars-and-organisations',
            icon: 'users',
            iconCss: 'text-emerald-500 bg-emerald-500/20',
            linkCss: 'group-hover:text-emerald-600 [&.router-link-exact-active]:text-emerald-600'
          },
          {
            title: t('menu.sectionNetwork.UIAdvance'),
            path: 'pages-uiadvance',
            icon: 'globe',
            iconCss: 'text-purple-500 bg-purple-500/20',
            linkCss: 'group-hover:text-purple-600 [&.router-link-exact-active]:text-purple-600'
          },
          {
            title: t('menu.sectionNetwork.worldForumMediation'),
            path: 'pages-uia-world-forum-of-mediation-centres',
            icon: 'scale-balanced',
            iconCss: 'text-sky-500 bg-sky-500/20',
            linkCss: 'group-hover:text-sky-600 [&.router-link-exact-active]:text-sky-600'
          }
        ]
      },
      resources: {
        title: t('menu.mainMenu.resources'),
        path: localePath({ name: 'documents-library' }),
        menuCss: 'w-120',
        gridCss: 'grid-cols-2',
        items: [
          {
            title: t('menu.sectionResources.magazine'),
            path: 'juriste-internationals',
            icon: 'newspaper',
            iconCss: 'text-pink-500 bg-pink-500/20',
            linkCss: 'group-hover:text-pink-600 [&.router-link-exact-active]:text-pink-600'
          },
          {
            title: t('menu.sectionResources.publications'),
            path: 'publications',
            icon: 'books',
            iconCss: 'text-orange-500 bg-orange-500/20',
            linkCss: 'group-hover:text-orange-600 [&.router-link-exact-active]:text-orange-600'
          },
          {
            title: t('menu.sectionResources.library'),
            path: 'documents-library',
            icon: 'books',
            iconCss: 'text-purple-500 bg-purple-500/20',
            linkCss: 'group-hover:text-purple-600 [&.router-link-exact-active]:text-purple-600'
          },
          {
            title: t('menu.sectionResources.podcasts'),
            path: 'pages-podcasts',
            icon: 'podcast',
            iconCss: 'text-indigo-500 bg-indigo-500/20',
            linkCss: 'group-hover:text-indigo-600 [&.router-link-exact-active]:text-indigo-600'
          }
        ]
      }
    }
  })

  return { menuConfig, menuTop, menuMain }
}
